const generarId = () => {
  const randomPart = () => Math.random().toString(36).slice(2, 10);
  return `${Date.now()}_${randomPart()}_${randomPart()}`;
};

export const TIPOS_COMPROBANTE = {
  FACTURA: "FACTURA",
  BOLETA: "BOLETA",
  NOTA_CREDITO: "NOTA_CREDITO",
  NOTA_DEBITO: "NOTA_DEBITO",
  RECIBO_HONORARIOS: "RECIBO_HONORARIOS",
};

export const TIPOS_DOCUMENTO = {
  DNI: "DNI",
  RUC: "RUC",
  OTRO: "OTRO",
};

export const CLASIFICACIONES_TRIBUTARIAS = {
  GRAVADO: "GRAVADO",
  EXONERADO: "EXONERADO",
  INAFECTO: "INAFECTO",
  EXPORTACION: "EXPORTACION",
};

export const ESTADOS_TRIBUTARIOS = {
  PENDIENTE_VALIDACION: "PENDIENTE_VALIDACION",
  VALIDADO: "VALIDADO",
  OBSERVADO: "OBSERVADO",
  ANULADO: "ANULADO",
};

const cleanText = (value) => (value == null ? "" : String(value).trim());
const cleanNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
};
const ensurePeriodo = (fecha) => {
  const text = cleanText(fecha);
  if (!text) return "";
  if (/^\d{4}-\d{2}$/.test(text)) return text;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text.slice(0, 7);
  return text;
};
const inferirTipoDocumento = (numeroDocumento) => {
  const text = cleanText(numeroDocumento);
  if (/^\d{8}$/.test(text)) return TIPOS_DOCUMENTO.DNI;
  if (/^\d{11}$/.test(text)) return TIPOS_DOCUMENTO.RUC;
  return TIPOS_DOCUMENTO.OTRO;
};
const basePorTipoOperacion = (tipoOperacion, importes) => {
  switch (tipoOperacion) {
    case CLASIFICACIONES_TRIBUTARIAS.EXONERADO:
      return {
        base_gravada: 0,
        base_exonerada: cleanNumber(importes.total),
        base_inafecta: 0,
        exportacion: 0,
        igv: 0,
      };
    case CLASIFICACIONES_TRIBUTARIAS.INAFECTO:
      return {
        base_gravada: 0,
        base_exonerada: 0,
        base_inafecta: cleanNumber(importes.total),
        exportacion: 0,
        igv: 0,
      };
    case CLASIFICACIONES_TRIBUTARIAS.EXPORTACION:
      return {
        base_gravada: 0,
        base_exonerada: 0,
        base_inafecta: 0,
        exportacion: cleanNumber(importes.total),
        igv: 0,
      };
    default:
      return {
        base_gravada: cleanNumber(importes.subtotal),
        base_exonerada: 0,
        base_inafecta: 0,
        exportacion: 0,
        igv: cleanNumber(importes.igv),
      };
  }
};

const buildDocumentoTributarioBase = ({
  empresaId,
  rucEmpresa,
  comprobante,
  tercero,
  importes,
  tipoOperacion,
  documentoRelacionado,
  origen,
}) => {
  const tipoCmp = cleanText(comprobante?.tipo || "FACTURA").toUpperCase();
  const serie = cleanText(comprobante?.serie || "");
  const numero = cleanText(comprobante?.numero || "");
  const fechaEmision = cleanText(comprobante?.fechaEmision || "");
  const fechaVencimiento = cleanText(comprobante?.fechaVencimiento || comprobante?.fechaEmision || "");
  const moneda = cleanText(comprobante?.moneda || "PEN").toUpperCase();
  const tipoCambio = cleanNumber(comprobante?.tipoCambio || 1);
  const subtotal = cleanNumber(importes?.subtotal ?? importes?.base_gravada ?? 0);
  const igv = cleanNumber(importes?.igv || 0);
  const total = cleanNumber(importes?.total ?? subtotal + igv);
  const tipoDocTercero = inferirTipoDocumento(tercero?.numeroDocumento || tercero?.numero_documento || "");
  const baseData = basePorTipoOperacion(tipoOperacion, { subtotal, igv, total });
  const importeOriginal = cleanNumber(importes?.importeOriginal ?? importes?.importe_original ?? total);
  const importeConvertido = moneda === "PEN"
    ? importeOriginal
    : cleanNumber((importeOriginal * (tipoCambio || 1)).toFixed(2));

  return {
    id: generarId(),
    empresa_id: cleanText(empresaId || rucEmpresa),
    ruc_emisor: cleanText(rucEmpresa),
    tipo_comprobante: tipoCmp,
    serie,
    numero,
    fecha_emision: fechaEmision,
    fecha_vencimiento: fechaVencimiento,
    periodo: ensurePeriodo(fechaEmision),
    moneda,
    tipo_cambio: tipoCambio,
    tercero: {
      tipo_documento: tipoDocTercero,
      numero_documento: cleanText(tercero?.numeroDocumento || tercero?.numero_documento || ""),
      razon_social: cleanText(tercero?.razonSocial || tercero?.razon_social || ""),
    },
    importes: {
      subtotal,
      base_gravada: baseData.base_gravada,
      base_exonerada: baseData.base_exonerada,
      base_inafecta: baseData.base_inafecta,
      exportacion: baseData.exportacion,
      igv,
      otros_tributos: cleanNumber(importes?.otrosTributos ?? importes?.otros_tributos ?? 0),
      total,
      importe_original: importeOriginal,
      importe_convertido: importeConvertido,
    },
    documento_relacionado: documentoRelacionado
      ? {
          tipo_documento_referencia: cleanText(documentoRelacionado?.tipoDocumento || documentoRelacionado?.tipo_documento_referencia || "").toUpperCase(),
          serie_referencia: cleanText(documentoRelacionado?.serie || documentoRelacionado?.serie_referencia || ""),
          numero_referencia: cleanText(documentoRelacionado?.numero || documentoRelacionado?.numero_referencia || ""),
          motivo: cleanText(documentoRelacionado?.motivo || ""),
          tipo_nota: cleanText(documentoRelacionado?.tipoNota || documentoRelacionado?.tipo_nota || ""),
        }
      : null,
    tipo_operacion: cleanText(tipoOperacion || CLASIFICACIONES_TRIBUTARIAS.GRAVADO).toUpperCase(),
    estado_comercial: "REGISTRADO",
    estado_contable: "PENDIENTE",
    estado_tributario: ESTADOS_TRIBUTARIOS.PENDIENTE_VALIDACION,
    estado_sunat: "PENDIENTE",
    origen: origen
      ? {
          id: cleanText(origen?.id || ""),
          tipo: cleanText(origen?.tipo || ""),
          coleccion: cleanText(origen?.coleccion || ""),
        }
      : null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
};

export const normalizarDocumentoTributarioVenta = (input = {}) => buildDocumentoTributarioBase({
  empresaId: input.empresaId,
  rucEmpresa: input.rucEmpresa,
  comprobante: input.comprobante,
  tercero: {
    numeroDocumento: input.clienteRuc,
    razonSocial: input.clienteRazonSocial,
  },
  importes: {
    subtotal: input.subtotal,
    igv: input.igv,
    total: input.total,
    importeOriginal: input.importeOriginal,
    importe_original: input.importeOriginal,
    otrosTributos: input.otrosTributos,
  },
  tipoOperacion: input.tipoOperacion,
  documentoRelacionado: input.documentoRelacionado,
  origen: input.origen,
});

export const normalizarDocumentoTributarioCompra = (input = {}) => buildDocumentoTributarioBase({
  empresaId: input.empresaId,
  rucEmpresa: input.rucEmpresa,
  comprobante: input.comprobante,
  tercero: {
    numeroDocumento: input.proveedorRuc,
    razonSocial: input.proveedorRazonSocial,
  },
  importes: {
    subtotal: input.subtotal,
    igv: input.igv,
    total: input.total,
    importeOriginal: input.importeOriginal,
    importe_original: input.importeOriginal,
    otrosTributos: input.otrosTributos,
  },
  tipoOperacion: input.tipoOperacion,
  documentoRelacionado: input.documentoRelacionado,
  origen: input.origen,
});

export const validarDocumentoTributario = (documento = {}) => {
  const errors = [];
  if (!cleanText(documento.ruc_emisor)) errors.push("RUC emisor obligatorio");
  if (!cleanText(documento.tipo_comprobante)) errors.push("Tipo de comprobante obligatorio");
  if (!cleanText(documento.serie)) errors.push("Serie obligatoria");
  if (!cleanText(documento.numero)) errors.push("Número obligatorio");
  if (!cleanText(documento.fecha_emision)) errors.push("Fecha de emisión obligatoria");
  if (!cleanText(documento.periodo)) errors.push("Periodo obligatorio");
  if (!cleanText(documento.moneda)) errors.push("Moneda obligatoria");
  if (!cleanText(documento.tercero?.numero_documento)) errors.push("Documento del tercero obligatorio");
  if (!documento.importes || typeof documento.importes !== "object") errors.push("Importes obligatorios");
  if (cleanNumber(documento.importes?.total) <= 0) errors.push("Total debe ser mayor a cero");
  if (cleanNumber(documento.importes?.igv) < 0) errors.push("IGV no puede ser negativo");
  if (documento.moneda !== "PEN" && cleanNumber(documento.tipo_cambio) <= 0) errors.push("Tipo de cambio obligatorio para moneda extranjera");
  if (documento.documento_relacionado && !cleanText(documento.documento_relacionado.numero_referencia)) {
    errors.push("Nota debe tener número de referencia");
  }
  return { ok: errors.length === 0, errors };
};

export const crearRegistroTributario = (documento = {}, extra = {}) => {
  const validation = validarDocumentoTributario(documento);
  const registro = {
    id: generarId(),
    documento_tributario_id: cleanText(documento.id),
    empresa_id: cleanText(documento.empresa_id || extra.empresaId || ""),
    ruc_empresa: cleanText(documento.ruc_emisor || extra.rucEmpresa || ""),
    tipo_registro: cleanText(extra.tipoRegistro || "GENERIC").toUpperCase(),
    periodo: cleanText(documento.periodo || extra.periodo || ""),
    origen: documento.origen ? { ...documento.origen } : null,
    tipo_comprobante: cleanText(documento.tipo_comprobante || ""),
    estado: validation.ok ? ESTADOS_TRIBUTARIOS.PENDIENTE_VALIDACION : ESTADOS_TRIBUTARIOS.OBSERVADO,
    estado_tributario: documento.estado_tributario || ESTADOS_TRIBUTARIOS.PENDIENTE_VALIDACION,
    documento_asociado: {
      id: cleanText(documento.id),
      tipo: cleanText(documento.tipo_comprobante || ""),
      serie: cleanText(documento.serie || ""),
      numero: cleanText(documento.numero || ""),
      fecha_emision: cleanText(documento.fecha_emision || ""),
    },
    errores_validacion: validation.errors,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  return registro;
};

export const validarRegistroTributario = (registro = {}) => {
  const errors = [];
  if (!cleanText(registro.documento_tributario_id)) errors.push("Documento tributario obligatorio");
  if (!cleanText(registro.ruc_empresa)) errors.push("RUC empresa obligatorio");
  if (!cleanText(registro.periodo)) errors.push("Periodo obligatorio");
  if (!cleanText(registro.tipo_registro)) errors.push("Tipo de registro obligatorio");
  return { ok: errors.length === 0, errors };
};
